import {
	ApplicationRef,
	ComponentRef,
	createComponent,
	Directive,
	ElementRef,
	EnvironmentInjector,
	HostListener,
	inject,
	Injector,
	Input,
	TemplateRef,
} from '@angular/core';
import { TooltipComponent } from './tooltip.component';

@Directive({
	selector: '[appTooltip]',
	standalone: true,
})
export class TooltipDirective {
	private readonly el = inject(ElementRef);
	private readonly appRef = inject(ApplicationRef);
	private readonly Injector = inject(Injector);
	private readonly EnvironmentInjector = inject(EnvironmentInjector);
	private uniqueId = Math.random().toString(36).slice(2);
	@Input('appTooltip') tooltipText: string | TemplateRef<any> = '';
	@Input() tooltipOrientation: {
		top?: boolean;
		bottom?: boolean;
		left?: boolean;
		right?: boolean;
		middle?: boolean;
	} = {
		top: true,
		left: true,
	};
	@Input() tooltipDelay: number; //millisecond
	@Input() tooltipNgStyle: { [key: string]: string } | Array<{ [key: string]: string }> = {};

	private tooltipComponentRef: ComponentRef<TooltipComponent> | null = null;
	private showTimeout: any;
	private hostHovered = false;
	private tooltipHovered = false;
	private tooltipMouseEnterUnlisten: (() => void) | null = null;
	private tooltipMouseLeaveUnlisten: (() => void) | null = null;

	constructor() {}

	@HostListener('mouseenter')
	onMouseEnter() {
		this.hostHovered = true;
		if (!this.tooltipText) return;
		this.showTimeout = setTimeout(() => {
			this.createTooltip();
		}, this.tooltipDelay);
	}

	@HostListener('mouseleave')
	onMouseLeave() {
		this.hostHovered = false;
		if (this.showTimeout) {
			clearTimeout(this.showTimeout);
			this.showTimeout = null;
		}

		if (typeof this.tooltipText === 'string') {
			this.destroyTooltip();
			return;
		}

		//Delay na destruição pra conseguir dar hover no template
		setTimeout(() => {
			if (!this.tooltipHovered && !this.hostHovered) {
				this.destroyTooltip();
			}
		}, 500);
	}

	createTooltip() {
		//Não cria se a tooltip já foi gerada
		if (this.tooltipComponentRef) return;

		//Cria componente dinâmicamente
		this.tooltipComponentRef = createComponent(TooltipComponent, {
			environmentInjector: this.EnvironmentInjector,
			elementInjector: this.Injector,
		});

		//Define o conteúdo
		if (this.tooltipText instanceof TemplateRef) {
			this.tooltipComponentRef.instance.template = this.tooltipText;
			this.tooltipComponentRef.instance.content = '';
		} else {
			this.tooltipComponentRef.instance.template = null;
			this.tooltipComponentRef.instance.content = this.tooltipText;
		}

		this.tooltipComponentRef.instance.tooltipNgStyles = this.tooltipNgStyle;
		this.tooltipComponentRef.instance.orientation = this.tooltipOrientation;

		//Calcular posição
		this.calculatePosition();

		//Attach DOM
		this.el.nativeElement.append(this.tooltipComponentRef.location.nativeElement);
		this.appRef.attachView(this.tooltipComponentRef.hostView);
		this.tooltipComponentRef.changeDetectorRef.detectChanges();

		if (typeof this.tooltipText === 'string') {
			return;
		}

		//Adiciona listeners no tooltip pra manter ele aberto quando movendo no template
		const tooltipElement = this.tooltipComponentRef.location.nativeElement as HTMLElement;
		const mouseEnter = () => {
			this.tooltipHovered = true;
		};
		const mouseLeave = () => {
			this.tooltipHovered = false;
			setTimeout(() => {
				if (!this.hostHovered && !this.tooltipHovered) {
					this.destroyTooltip();
				}
			}, 50);
		};
		tooltipElement.addEventListener('mouseenter', mouseEnter);
		tooltipElement.addEventListener('mouseleave', mouseLeave);

		this.tooltipMouseEnterUnlisten = () => tooltipElement.removeEventListener('mouseenter', mouseEnter);
		this.tooltipMouseLeaveUnlisten = () => tooltipElement.removeEventListener('mouseleave', mouseLeave);
	}

	private calculatePosition() {
		(this.el.nativeElement as HTMLElement).style['anchorName'] = `--tooltip-anchor-${this.uniqueId}`;
		this.tooltipComponentRef.instance.anchor = `--tooltip-anchor-${this.uniqueId}`;
		this.tooltipComponentRef.instance.orientation = this.tooltipOrientation;
	}

	private destroyTooltip() {
		if (this.tooltipComponentRef) {
			if (this.tooltipMouseEnterUnlisten) {
				this.tooltipMouseEnterUnlisten();
				this.tooltipMouseEnterUnlisten = null;
			}

			if (this.tooltipMouseLeaveUnlisten) {
				this.tooltipMouseLeaveUnlisten();
				this.tooltipMouseLeaveUnlisten = null;
			}

			this.appRef.detachView(this.tooltipComponentRef.hostView);
			this.tooltipComponentRef.destroy();
			this.tooltipComponentRef = null;
			this.tooltipHovered = false;
			this.el.nativeElement.style['anchorName'] = '';
		}
	}
}
